"use strict";
// Copyright (C) Microsoft Corporation. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.setGlobalSearchScope = exports.isGlobalFocusLoading = exports.hasGlobalFocus = exports.BodyTrappable = exports.InnerBodyTrappable = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importStar(require("react"));
const focusManager_1 = require("../../core/managers/focusManager");
const generic_utility_1 = require("../../hooks/generic-utility");
const initialFocusFinder_1 = require("../../core/search/initialFocusFinder");
const trappableContext_1 = require("./trappableContext");
const cache_1 = require("../../core/cache");
class InnerBodyTrappable extends react_1.Component {
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    constructor(props) {
        super(props);
        this.setSearchScope = (element) => {
            this.lastRequestedSearchScope = element;
            this.currentFocusAtLastSearchScopeRequest = (0, focusManager_1.getCurrentlyFocusedInteractable)();
        };
        this.getSearchScope = () => {
            var _a, _b;
            const currentFocus = (0, focusManager_1.getCurrentlyFocusedInteractable)();
            // If we have a custom search scope & focus has not shifted to another element since
            // we last requested a custom search scope, use that customs search scope
            if (this.lastRequestedSearchScope &&
                (!currentFocus || currentFocus === this.currentFocusAtLastSearchScopeRequest)) {
                return this.lastRequestedSearchScope;
            }
            else {
                return (_b = (_a = globalThis.document) === null || _a === void 0 ? void 0 : _a.body) !== null && _b !== void 0 ? _b : undefined;
            }
        };
        this.hasCustomSearchScope = () => {
            var _a, _b;
            return this.getSearchScope() !== ((_b = (_a = globalThis.document) === null || _a === void 0 ? void 0 : _a.body) !== null && _b !== void 0 ? _b : undefined);
        };
    }
    componentDidMount() {
        this.props.bodyTrappableRef.current = this;
    }
    componentWillUnmount() {
        unregisterBodyTrappable(this);
    }
    render() {
        return react_1.default.createElement(react_1.default.Fragment, null, this.props.children);
    }
}
exports.InnerBodyTrappable = InnerBodyTrappable;
/**
 * A component for specifying that the document.body itself should be the focus container for all content. This can be set as
 * a self-closing tag or a wrapper for children. Note that if this is used as a wrapper, it does not affect the layout of the
 * page, and instead is used to create a loading context so that children of the `BodyTrappable` can register loading content
 * through the `useTrappableLoading` hook.
 *
 * @param loading Whether content under the `BodyTrappable` is loading. Once set to true initial focus will be set. If loading
 * content is deeply nested `useTrappableLoading` may be cleaner
 *
 * @param loadingTimeoutMs The timeout before initial focus is set regardless of loading state
 */
const BodyTrappable = ({ children, loadingTimeoutMs, loading, retainLoadedState }) => {
    var _a, _b;
    const bodyTrappableRef = (0, react_1.useRef)();
    const bodyRef = (0, react_1.useRef)((_b = (_a = globalThis.document) === null || _a === void 0 ? void 0 : _a.body) !== null && _b !== void 0 ? _b : null);
    const hasLoaded = (0, react_1.useRef)(false);
    // The list of refs that are loading for the context
    const loadingRefs = (0, react_1.useRef)([]);
    // Whether the initial page load is still happening
    const pageLoading = (0, react_1.useRef)(true);
    // Register the body trappable as loading until loading is false, nothing under the context is loading,
    // and the page has rendered
    (0, react_1.useEffect)(() => {
        const bodyTrappable = bodyTrappableRef.current;
        if (!bodyTrappable) {
            return;
        }
        registerLoadingBodyTrappable(bodyTrappable);
    }, []);
    const initializeFocus = (0, react_1.useCallback)((forceFocus) => {
        const bodyTrappable = bodyTrappableRef.current;
        if (!bodyTrappable) {
            return;
        }
        // Strict conditions, we must always adhere to these
        if (pageLoading.current) {
            return;
        }
        // Lax conditions, if we are past the loadingTimeout then ignore loading requests
        if ((loading || loadingRefs.current.length !== 0) && !forceFocus) {
            return;
        }
        registerLoadedBodyTrappable(bodyTrappable);
        hasLoaded.current = true;
        if (loadingBodyTrappables.size !== 0) {
            return;
        }
        (0, cache_1.clearCache)();
        // If we have already focused, we only want to re-trigger an initial search if there
        // is no currently visible interactable element.
        (0, focusManager_1.pruneCurrentlyFocusedInteractable)();
        const currentFocus = (0, focusManager_1.getCurrentlyFocusedInteractable)();
        if (!currentFocus || bodyTrappable.hasCustomSearchScope()) {
            (0, focusManager_1.clearCurrentFocus)();
            (0, focusManager_1.resetLastRememberedGlobalFocus)();
            (0, initialFocusFinder_1.shiftFocus)(bodyTrappable.getSearchScope());
        }
    }, [loading]);
    (0, generic_utility_1.usePostInitialRenderCallback)((0, react_1.useCallback)(() => {
        pageLoading.current = false;
        initializeFocus();
    }, [initializeFocus]));
    const onLoadingRequest = (0, react_1.useCallback)(() => {
        if (!retainLoadedState || !hasLoaded.current) {
            registerLoadingBodyTrappable(bodyTrappableRef.current);
        }
    }, [retainLoadedState]);
    (0, react_1.useEffect)(() => {
        if (!loading) {
            initializeFocus();
        }
        else {
            onLoadingRequest();
        }
    }, [loading, onLoadingRequest]);
    (0, react_1.useEffect)(() => {
        let loadingTimeout;
        if (loadingTimeoutMs && loadingTimeoutMs > 0) {
            loadingTimeout = setTimeout(() => {
                initializeFocus(true);
            }, loadingTimeoutMs);
        }
        return () => {
            if (loadingTimeout) {
                clearTimeout(loadingTimeout);
            }
        };
    }, []);
    /*
     * Trappable context
     */
    const trappableContextValue = (0, react_1.useMemo)(() => ({
        loadingRefs,
        initializeFocus,
        trappableRef: bodyRef,
        onAddLoadingRef: onLoadingRequest,
    }), [initializeFocus]);
    return (react_1.default.createElement(trappableContext_1.TrappableContext.Provider, { value: trappableContextValue },
        react_1.default.createElement(InnerBodyTrappable, { bodyTrappableRef: bodyTrappableRef }, children)));
};
exports.BodyTrappable = BodyTrappable;
/*
If a BodyTrappable is loading, we want to prevent any other BodyTrappables from focusing. This is because we may want to
use the loading context in multiple places on the page, and we don't want to focus until all loading contexts are done loading.
*/
// BodyTrappable
const registeredBodyTrappables = new Set();
// BodyTrappables that are currently loading
const loadingBodyTrappables = new Set();
const registerLoadingBodyTrappable = (bodyTrappable) => {
    if (bodyTrappable) {
        registeredBodyTrappables.add(bodyTrappable);
        loadingBodyTrappables.add(bodyTrappable);
    }
};
const registerLoadedBodyTrappable = (bodyTrappable) => {
    if (bodyTrappable) {
        registeredBodyTrappables.add(bodyTrappable);
        loadingBodyTrappables.delete(bodyTrappable);
    }
};
const unregisterBodyTrappable = (bodyTrappable) => {
    if (bodyTrappable) {
        registeredBodyTrappables.delete(bodyTrappable);
        loadingBodyTrappables.delete(bodyTrappable);
    }
};
const hasGlobalFocus = () => registeredBodyTrappables.size !== 0;
exports.hasGlobalFocus = hasGlobalFocus;
const isGlobalFocusLoading = () => loadingBodyTrappables.size !== 0;
exports.isGlobalFocusLoading = isGlobalFocusLoading;
const setGlobalSearchScope = (element) => {
    for (const bodyTrappable of registeredBodyTrappables) {
        bodyTrappable.setSearchScope(element);
    }
};
exports.setGlobalSearchScope = setGlobalSearchScope;
//# sourceMappingURL=data:application/json;base64,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